<!-- eslint-disable prettier/prettier -->
<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';

const props = defineProps({
  activeColor: { type: String, default: '' },
  resetLabel: { type: String, default: 'Reset to default' },
  customHexPlaceholder: { type: String, default: 'hex' },
  customHexApply: { type: String, default: 'OK' },
});

const emit = defineEmits(['select', 'reset']);

const isOpen = ref(false);
const dropdownRef = ref(null);
const triggerRef = ref(null);
const customHex = ref('');
const nativeColorInput = ref(null);
const HEX_PREFIX = '#';

const SWATCHES = [
  '#000000', '#434343', '#666666', '#999999', '#b7b7b7',
  '#cccccc', '#d9d9d9', '#efefef', '#f3f3f3', '#ffffff',
  '#980000', '#ff0000', '#ff9900', '#ffff00', '#00ff00',
  '#00ffff', '#4a86e8', '#0000ff', '#9900ff', '#ff00ff',
  '#e6b8af', '#f4cccc', '#fce5cd', '#fff2cc', '#d9ead3',
  '#d0e0e3', '#c9daf8', '#cfe2f3', '#d9d2e9', '#ead1dc',
  '#dd7e6b', '#ea9999', '#f9cb9c', '#ffe599', '#b6d7a8',
  '#a2c4c9', '#a4c2f4', '#9fc5e8', '#b4a7d6', '#d5a6bd',
  '#cc4125', '#e06666', '#f6b26b', '#ffd966', '#93c47d',
  '#76a5af', '#6d9eeb', '#6fa8dc', '#8e7cc3', '#c27ba0',
  '#a61c00', '#cc0000', '#e69138', '#f1c232', '#6aa84f',
  '#45818e', '#3c78d8', '#3d85c6', '#674ea7', '#a64d79',
  '#85200c', '#990000', '#b45f06', '#bf9000', '#38761d',
  '#134f5c', '#1155cc', '#0b5394', '#351c75', '#741b47',
  '#5b0f00', '#660000', '#783f04', '#7f6000', '#274e13',
  '#0c343d', '#1c4587', '#073763', '#20124d', '#4c1130',
];

function toggle() {
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    customHex.value = '';
  }
}

function select(color) {
  emit('select', color);
  isOpen.value = false;
}

function reset() {
  emit('reset');
  isOpen.value = false;
}

function applyCustomHex() {
  const hex = customHex.value.replace(/^#/, '').trim();
  if (/^[0-9a-fA-F]{3}([0-9a-fA-F]{3})?$/.test(hex)) {
    emit('select', `#${hex}`);
    isOpen.value = false;
  }
}

function onNativeColorChange(event) {
  emit('select', event.target.value);
  isOpen.value = false;
}

function onClickOutside(event) {
  if (
    isOpen.value &&
    dropdownRef.value &&
    !dropdownRef.value.contains(event.target) &&
    triggerRef.value &&
    !triggerRef.value.contains(event.target)
  ) {
    isOpen.value = false;
  }
}

onMounted(() => document.addEventListener('click', onClickOutside));
onBeforeUnmount(() => document.removeEventListener('click', onClickOutside));
</script>

<template>
  <div class="relative">
    <button
      ref="triggerRef"
      type="button"
      class="size-8 inline-flex items-center justify-center rounded hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
      @click="toggle"
    >
      <span class="i-lucide-baseline size-4" />
      <span
        class="absolute bottom-1 left-1.5 right-1.5 h-0.5 rounded-full"
        :style="{ backgroundColor: activeColor || '#000000' }"
      />
    </button>
    <div
      v-if="isOpen"
      ref="dropdownRef"
      class="absolute top-full left-0 mt-1 z-50 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg p-2 w-[232px]"
    >
      <button
        type="button"
        class="w-full text-left text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded px-2 py-1 mb-1.5"
        @click="reset"
      >
        {{ resetLabel }}
      </button>
      <div class="grid grid-cols-10 gap-0.5">
        <button
          v-for="color in SWATCHES"
          :key="color"
          type="button"
          class="size-5 rounded-sm border border-slate-200 dark:border-slate-600 hover:scale-125 transition-transform cursor-pointer"
          :class="{
            'ring-2 ring-n-brand ring-offset-1': activeColor === color,
          }"
          :style="{ backgroundColor: color }"
          :title="color"
          @click="select(color)"
        />
      </div>
      <div
        class="flex items-center gap-1.5 mt-2 pt-2 border-t border-slate-200 dark:border-slate-700"
      >
        <button
          type="button"
          class="relative size-6 rounded border border-slate-200 dark:border-slate-600 cursor-pointer overflow-hidden flex-shrink-0"
          :style="{ backgroundColor: activeColor || '#000000' }"
          @click.stop="nativeColorInput?.click()"
        >
          <input
            ref="nativeColorInput"
            type="color"
            class="absolute inset-0 opacity-0 cursor-pointer"
            :value="activeColor || '#000000'"
            @change="onNativeColorChange"
            @click.stop
          />
        </button>
        <div class="flex items-center flex-1 gap-1">
          <span class="text-xs text-slate-400 dark:text-slate-500">{{
            HEX_PREFIX
          }}</span>
          <input
            v-model="customHex"
            type="text"
            maxlength="7"
            class="reset-base flex-1 text-xs bg-transparent border-none outline-none text-slate-800 dark:text-slate-200 w-0"
            :placeholder="props.customHexPlaceholder"
            @keydown.enter.prevent="applyCustomHex"
          />
        </div>
        <button
          type="button"
          class="text-[10px] font-medium text-n-brand hover:text-n-brand/80 transition-colors flex-shrink-0"
          @click="applyCustomHex"
        >
          {{ props.customHexApply }}
        </button>
      </div>
    </div>
  </div>
</template>
